import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCheckCircle, faCircle, faBan, faCamera, faUser, faBuilding, faListCheck, faPlus,
} from '@fortawesome/free-solid-svg-icons';
import TaskListEditor from '../../ClientRequests/TaskListEditor';
import EmptyState from '../../EmptyState';
import './clientBookingTasks.css';

const STATUS_META = {
    completed: { label: 'Done', icon: faCheckCircle, cls: 'done' },
    skipped: { label: 'Skipped', icon: faBan, cls: 'skipped' },
    pending: { label: 'Pending', icon: faCircle, cls: 'pending' },
};

const SOURCE_META = {
    client: { label: 'All units', icon: faUser },
    property: { label: 'Property', icon: faBuilding },
    booking: { label: 'This booking', icon: faListCheck },
};

// Focused tasks-only editor for this booking's ad-hoc (source='booking') tasks.
// Submits an edit_booking request (staff-reviewed) with the full desired list.
function BookingTasksModal({ bookingId, initial, onClose, onSubmitted }) {
    const original = (initial || []).map((t) => ({
        id: t.id, instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required,
    }));
    const [tasks, setTasks] = useState(
        (initial || []).map((t) => ({ id: t.id, instruction: t.instruction, is_report_required: !!t.is_report_required }))
    );
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const cleaned = tasks
        .map((t) => ({ id: t.id ?? null, instruction: (t.instruction || '').trim(), is_report_required: !!t.is_report_required }))
        .filter((t) => t.instruction);
    const changed = JSON.stringify(cleaned) !== JSON.stringify(original);

    const submit = async () => {
        setSaving(true);
        setError('');
        try {
            await axios.post(`/api/client-requests/requests/bookings/${bookingId}/edit`, { tasks: cleaned });
            onSubmitted();
        } catch (err) {
            setError(err?.response?.data?.error || 'Could not submit your task changes. Please try again.');
            setSaving(false);
        }
    };

    return (
        <div className="cbt-modal__backdrop" onClick={onClose}>
            <div className="cbt-modal" onClick={(e) => e.stopPropagation()}>
                <div className="cbt-modal__head">
                    <h3><FontAwesomeIcon icon={faListCheck} /> Tasks for this cleaning</h3>
                    <button className="cbt-modal__x" onClick={onClose} aria-label="Close">&times;</button>
                </div>
                <p className="cbt-modal__intro">
                    One-off tasks just for this booking — separate from your standing all-units and property tasks.
                    Changes are reviewed by our team before they take effect.
                </p>
                <TaskListEditor
                    tasks={tasks}
                    onChange={setTasks}
                    addLabel="Add a task for this cleaning"
                    emptyHint="No extra tasks for this booking yet."
                    disabled={saving}
                />
                {error && <div className="cbt-modal__error">{error}</div>}
                <div className="cbt-modal__actions">
                    <button className="cbt-btn cbt-btn--ghost" onClick={onClose} disabled={saving}>Cancel</button>
                    <button className="cbt-btn cbt-btn--primary" onClick={submit} disabled={saving || !changed}>
                        {saving ? 'Submitting…' : 'Submit for review'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const isVideoFile = (url) => /\.(mp4|mov|avi|mkv)$/i.test(url || '');
const videoThumb = (r) => r.thumbnail_url || (r.file_url || '').replace(/\.mp4$/i, '.jpg');

// One album page per attachment (photo OR video), grouped by task; tasks without
// attachments get a text-only page — so ‹ › walks EVERY task in canonical order.
const buildAlbumPages = (tasks) => {
    const pages = [];
    (tasks || []).forEach((task, taskIdx) => {
        const slides = [
            ...(task.photo_url ? [{ uri: task.photo_url, video: false, kind: 'reference' }] : []),
            ...((task.reports || []).map((r) => ({
                uri: r.file_url, video: isVideoFile(r.file_url), kind: 'report', comment: r.comment,
            }))),
        ];
        if (slides.length === 0) {
            pages.push({ taskIdx, task, slide: null, photoIdx: 0, photoCount: 0 });
        } else {
            slides.forEach((slide, photoIdx) => pages.push({ taskIdx, task, slide, photoIdx, photoCount: slides.length }));
        }
    });
    return pages;
};

export default function ClientBookingTasks({ bookingId, canManageTasks = false, onSubmitted }) {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [albumIndex, setAlbumIndex] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [flash, setFlash] = useState('');

    const fetchTasks = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/client-requests/bookings/${bookingId}/tasks`);
            setTasks(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            console.error('Error loading booking tasks:', err);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    }, [bookingId]);

    useEffect(() => { fetchTasks(); }, [fetchTasks]);

    // Auto-hide the green confirmation after a few seconds (matches admin Tasks).
    useEffect(() => {
        if (!flash) return undefined;
        const t = setTimeout(() => setFlash(''), 6000);
        return () => clearTimeout(t);
    }, [flash]);

    const pages = buildAlbumPages(tasks);
    const currentPage = albumIndex != null ? pages[albumIndex] : null;

    const openAlbum = (taskIdx, photoIdx) => {
        const page = pages.findIndex((p) => p.taskIdx === taskIdx && p.photoIdx === photoIdx);
        if (page >= 0) setAlbumIndex(page);
    };
    const closeAlbum = useCallback(() => setAlbumIndex(null), []);
    const albumPrev = useCallback(() => setAlbumIndex((i) => (i == null || i <= 0 ? i : i - 1)), []);
    const albumNext = useCallback(() => setAlbumIndex((i) => {
        const last = buildAlbumPages(tasks).length - 1;
        return i == null || i >= last ? i : i + 1;
    }), [tasks]);

    // Keyboard shortcuts while the album is open (matches the reports viewer).
    useEffect(() => {
        if (albumIndex == null) return undefined;
        const onKey = (e) => {
            if (e.key === 'Escape') { e.preventDefault(); closeAlbum(); }
            else if (e.key === 'ArrowLeft') { e.preventDefault(); albumPrev(); }
            else if (e.key === 'ArrowRight') { e.preventDefault(); albumNext(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [albumIndex, closeAlbum, albumPrev, albumNext]);

    if (loading) {
        return (
            <div className="app-loading">
                <div className="app-spinner" />
                <div className="app-spinner-text">Loading tasks…</div>
            </div>
        );
    }

    const adhocTasks = tasks.filter((t) => t.source === 'booking');

    return (
        <div className="cbt">
            {flash && <div className="cbt__flash">{flash}</div>}

            {tasks.some((t) => t.photo_url || (t.reports || []).length > 0) && (
                <p className="cbt__retention">Please note attachments are only available for 60 days from date of upload.</p>
            )}

            {tasks.length === 0 ? (
                <EmptyState icon={faListCheck} title="No tasks for this cleaning"
                    text="Standing and one-off tasks for this booking will appear here." />
            ) : tasks.map((t, i) => {
                const st = STATUS_META[t.status] || STATUS_META.pending;
                const src = SOURCE_META[t.source] || SOURCE_META.booking;
                const isPhoto = t.photo_url != null;
                return (
                    // Whole row opens the album at this task; thumbs jump to their photo.
                    <div
                        className={`cbt__row cbt__row--${st.cls}`}
                        key={t.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openAlbum(i, 0)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openAlbum(i, 0); } }}
                    >
                        <div className="cbt__main">
                            <div className="cbt__instr">
                                {/* Number matches the admin's task list for this booking */}
                                <span className="cbt__num">{i + 1}.</span>
                                {isPhoto && <img className="cbt__setupthumb" src={t.photo_url} alt=""
                                    onClick={(e) => { e.stopPropagation(); openAlbum(i, 0); }} />}
                                <span>{t.instruction}</span>
                            </div>
                            <div className="cbt__meta">
                                <span className="cbt__source"><FontAwesomeIcon icon={src.icon} /> {src.label}</span>
                                {t.is_report_required && <span className="cbt__req"><FontAwesomeIcon icon={faCamera} /> Photo required</span>}
                            </div>
                            {t.status === 'skipped' && t.skipping_reason && (
                                <div className="cbt__skipreason">Reason: {t.skipping_reason}</div>
                            )}
                            {t.reports && t.reports.length > 0 && (
                                <div className="cbt__proof">
                                    {t.reports.map((r, ri) => (
                                        <span key={r.id} className="cbt__proofwrap"
                                            onClick={(e) => { e.stopPropagation(); openAlbum(i, (isPhoto ? 1 : 0) + ri); }}>
                                            <img className="cbt__proofthumb"
                                                src={isVideoFile(r.file_url) ? videoThumb(r) : r.file_url} alt="proof" />
                                            {isVideoFile(r.file_url) && <span className="cbt__playbadge">&#9658;</span>}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                        <span className={`cbt__status cbt__status--${st.cls}`}>
                            <FontAwesomeIcon icon={st.icon} /> {st.label}
                        </span>
                    </div>
                );
            })}

            <div className="cbt__manage">
                <div className="cbt__note">
                    <strong>All units</strong> and <strong>Property</strong> tasks apply to every cleaning —
                    manage them on the <Link to="/client/tasks" className="cbt__link">Tasks page</Link>.
                </div>
                {canManageTasks && (
                    <button className="cbt__addbtn" onClick={() => setShowModal(true)}>
                        <FontAwesomeIcon icon={faPlus} /> Add extra task for this booking
                    </button>
                )}
            </div>

            {/* Task album — slide across every task; instruction under each picture */}
            {currentPage && (
                <div
                    className="cbt-album"
                    onClick={(e) => { if (e.target === e.currentTarget) closeAlbum(); }}
                >
                    <div className="cbt-album__counter">
                        Task {currentPage.taskIdx + 1} of {tasks.length}
                        {currentPage.photoCount > 1 ? ` · Photo ${currentPage.photoIdx + 1}/${currentPage.photoCount}` : ''}
                    </div>
                    <button type="button" className="modal-x cbt-album__x" onClick={closeAlbum} aria-label="Close">&times;</button>

                    {albumIndex > 0 && (
                        <button type="button" className="cbt-album__nav cbt-album__nav--prev" onClick={albumPrev} aria-label="Previous">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <polyline points="15 6 9 12 15 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}

                    <div className="cbt-album__content" onClick={(e) => e.stopPropagation()}>
                        {currentPage.slide ? (
                            currentPage.slide.video ? (
                                <video key={currentPage.slide.uri} className="cbt-album__video" controls autoPlay>
                                    <source src={currentPage.slide.uri} type="video/mp4" />
                                </video>
                            ) : (
                                <img className="cbt-album__img" src={currentPage.slide.uri} alt="Task attachment" />
                            )
                        ) : (
                            <div className="cbt-album__nophoto">
                                <span className="cbt-album__nophotoIcon">🖼️</span>
                                No photos for this task yet
                            </div>
                        )}
                        <div className="cbt-album__caption">
                            <div className="cbt-album__tags">
                                {currentPage.slide && (
                                    <span className={`cbt-album__tag ${currentPage.slide.kind === 'report' ? 'cbt-album__tag--report' : ''}`}>
                                        {currentPage.slide.kind === 'reference'
                                            ? 'How it should look'
                                            : currentPage.slide.video ? "Cleaner's video report" : "Cleaner's photo report"}
                                    </span>
                                )}
                                <span className={`cbt-album__tag cbt-album__tag--${(STATUS_META[currentPage.task.status] || STATUS_META.pending).cls}`}>
                                    {(STATUS_META[currentPage.task.status] || STATUS_META.pending).label}
                                </span>
                            </div>
                            <div className="cbt-album__instr">
                                <span className="cbt-album__instrNum">{currentPage.taskIdx + 1}.</span> {currentPage.task.instruction}
                            </div>
                            {currentPage.task.status === 'skipped' && currentPage.task.skipping_reason && (
                                <div className="cbt-album__comment">Skipped: {currentPage.task.skipping_reason}</div>
                            )}
                            {currentPage.slide?.kind === 'report' && currentPage.slide?.comment && (
                                <div className="cbt-album__comment">{currentPage.slide.comment}</div>
                            )}
                        </div>
                    </div>

                    {albumIndex < pages.length - 1 && (
                        <button type="button" className="cbt-album__nav cbt-album__nav--next" onClick={albumNext} aria-label="Next">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <polyline points="9 6 15 12 9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    )}
                </div>
            )}

            {showModal && (
                <BookingTasksModal
                    bookingId={bookingId}
                    initial={adhocTasks}
                    onClose={() => setShowModal(false)}
                    onSubmitted={() => {
                        setShowModal(false);
                        setFlash('Your task change was submitted for review — see the pending change above.');
                        fetchTasks();
                        if (onSubmitted) onSubmitted();
                    }}
                />
            )}
        </div>
    );
}
